declare const process: { env: Record<string, string | undefined> };

/**
 * Env must be read as static `process.env.NAME` expressions so webpack
 * DefinePlugin can inline them. Dynamic `process.env[key]` is empty in the browser.
 *
 * Native (Metro) does not load `.env` by default. Debug (`__DEV__`) defaults to
 * the dev stack; Release / Archive / TestFlight defaults to prod. Override via
 * `packages/budget/.env` (web) or shell env before `yarn ios` / `yarn android`.
 */

const DEV_API_URL = 'https://srh4zru4m4.execute-api.us-east-1.amazonaws.com/dev';
const PROD_API_URL = 'https://zrp1s3ciz2.execute-api.us-east-1.amazonaws.com/prod';
const DEV_WS_URL = 'wss://jtpzf8n2z7.execute-api.us-east-1.amazonaws.com/dev';
const PROD_WS_URL = 'wss://4hgywoq2og.execute-api.us-east-1.amazonaws.com/prod';
/** Debug: deployed App Runner (same as /budget/dev/ai-assistant-url). Override with AI_ASSISTANT_URL=http://localhost:4020 when running `yarn dev` in packages/ai-assistant. */
const DEV_ASSISTANT_URL = 'https://smjkjnht2u.us-east-1.awsapprunner.com';
const PROD_ASSISTANT_URL = 'https://unbdxzmtmt.us-east-1.awsapprunner.com';
const DEFAULT_ASSISTANT_APP_ID = 'moneylens';

export const IS_DEVELOPMENT =
  typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV !== 'production';

function defaultWsForApi(apiUrl: string): string {
  if (apiUrl.includes(PROD_API_URL) || /\/prod\/?$/.test(apiUrl)) {
    return PROD_WS_URL;
  }
  return DEV_WS_URL;
}

/** MoneyLens AI Google Cloud project — public OAuth client IDs (not secrets). */
const DEFAULT_GOOGLE_CLIENT_ID_WEB =
  '710263200575-bt821o9sv7o6n33d1ghjja04jb5md45o.apps.googleusercontent.com';
const DEFAULT_GOOGLE_CLIENT_ID_IOS =
  '710263200575-jl12ive2qm578on9rl6lr8n2le7jgub8.apps.googleusercontent.com';
/** Android debug — package `com.fma.moneylens` + debug keystore SHA-1. */
const DEFAULT_GOOGLE_CLIENT_ID_ANDROID_DEV =
  '710263200575-22g2tiij002u5o6ndvl38bavo0a5v957.apps.googleusercontent.com';
/** Android release — package `com.fma.moneylens` + release keystore SHA-1. */
const DEFAULT_GOOGLE_CLIENT_ID_ANDROID =
  '710263200575-e8ckkp85i2te0rhl8suv2bptp7kapvrn.apps.googleusercontent.com';

/** Shared native bundle / application id + OAuth custom scheme. */
const DEFAULT_NATIVE_OAUTH_SCHEME = 'com.fma.moneylens';

/**
 * ai-assistant is plain Node/App Runner — no API Gateway `/dev` stage.
 * Strip mistaken `/dev` when pointed at localhost.
 */
function normalizeAssistantUrl(raw: string): string {
  let url = raw.trim().replace(/\/$/, '');
  if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/dev$/i.test(url)) {
    url = url.replace(/\/dev$/i, '');
  }
  return url;
}

export const BUDGET_API_URL =
  process.env.BUDGET_API_URL || (IS_DEVELOPMENT ? DEV_API_URL : PROD_API_URL);

/**
 * App-agnostic streaming AI assistant (App Runner / local Node).
 * Thread + SSE chat live here; insights stay on BUDGET_API_URL.
 * Prefer AI_ASSISTANT_URL; BUDGET_ASSISTANT_URL kept as legacy alias.
 */
export const AI_ASSISTANT_URL = normalizeAssistantUrl(
  process.env.AI_ASSISTANT_URL ||
    process.env.BUDGET_ASSISTANT_URL ||
    (IS_DEVELOPMENT ? DEV_ASSISTANT_URL : PROD_ASSISTANT_URL),
);

/** App id registered in ai-assistant `apps` / APP_CONFIGS. */
export const AI_ASSISTANT_APP_ID =
  process.env.AI_ASSISTANT_APP_ID || DEFAULT_ASSISTANT_APP_ID;

/** @deprecated Use AI_ASSISTANT_URL */
export const BUDGET_ASSISTANT_URL = AI_ASSISTANT_URL;

/**
 * WebSocket URL for import streaming (CloudFormation `WebSocketApiUrl`).
 * Follows the selected API stage when BUDGET_WS_URL is unset (native Metro
 * does not load `.env`).
 */
export const BUDGET_WS_URL = (
  process.env.BUDGET_WS_URL || defaultWsForApi(BUDGET_API_URL)
).replace(/\/$/, '');

/** Google OAuth client IDs — set via env (see docs/AUTH_SETUP.md). */
export const GOOGLE_CLIENT_IDS = {
  ios: process.env.BUDGET_GOOGLE_CLIENT_ID_IOS || DEFAULT_GOOGLE_CLIENT_ID_IOS,
  androidDev:
    process.env.BUDGET_GOOGLE_CLIENT_ID_ANDROID_DEV || DEFAULT_GOOGLE_CLIENT_ID_ANDROID_DEV,
  androidProd:
    process.env.BUDGET_GOOGLE_CLIENT_ID_ANDROID || DEFAULT_GOOGLE_CLIENT_ID_ANDROID,
  web: process.env.BUDGET_GOOGLE_CLIENT_ID_WEB || DEFAULT_GOOGLE_CLIENT_ID_WEB,
};

/**
 * Native custom scheme → `{scheme}:/oauth`.
 * Must match iOS Info.plist + Android applicationId / AndroidManifest.
 */
export const GOOGLE_OAUTH_REDIRECT_SCHEME =
  process.env.BUDGET_GOOGLE_OAUTH_SCHEME || DEFAULT_NATIVE_OAUTH_SCHEME;

/** Firebase Web Push certificate (Cloud Messaging → Web Push certificates). Public. */
const DEFAULT_FIREBASE_VAPID_KEY =
  'BK75N26x544RD8DeBnHoHC_H0YxhSrqUDqN7o8m8DUdjXPMfqPsm0Bk3TN0gQm2sMm8asLpU9Tlm3wA263ubkT4';

export const FIREBASE_VAPID_KEY = process.env.BUDGET_FIREBASE_VAPID_KEY || DEFAULT_FIREBASE_VAPID_KEY;

/**
 * Sign in with Apple — web Services ID (public, not a secret).
 * Must match Apple Developer → Identifiers → Services ID associated with App ID `com.fma.moneylens`.
 */
const DEFAULT_APPLE_SERVICES_ID = 'com.fma.moneylens.web';
const DEFAULT_APPLE_WEB_REDIRECT_URI = 'https://moneylensai.ca/oauth/apple';

export const APPLE_SERVICES_ID =
  process.env.BUDGET_APPLE_SERVICES_ID || DEFAULT_APPLE_SERVICES_ID;

export function getAppleWebRedirectUri(): string {
  if (process.env.BUDGET_APPLE_REDIRECT_URI) {
    return process.env.BUDGET_APPLE_REDIRECT_URI;
  }
  if (typeof globalThis !== 'undefined') {
    const location = (globalThis as { location?: { origin?: string } }).location;
    const origin = location?.origin;
    if (origin && /localhost|127\.0\.0\.1/.test(origin)) {
      return `${origin.replace(/\/$/, '')}/oauth/apple`;
    }
  }
  return DEFAULT_APPLE_WEB_REDIRECT_URI;
}

export function getGoogleClientId(platform: 'ios' | 'android' | 'web' = 'web'): string {
  if (platform === 'web') return GOOGLE_CLIENT_IDS.web;
  // Android InAppBrowser uses custom scheme `com.fma.moneylens:/oauth`.
  // New Android OAuth clients have that redirect method disabled by default
  // ("Custom URI scheme is not enabled for your Android client"). The iOS
  // client is bound to the same bundle/package and already allows this scheme.
  // Android client IDs stay in GOOGLE_CLIENT_IDS for Play / GIS later.
  if (platform === 'ios' || platform === 'android') return GOOGLE_CLIENT_IDS.ios;
  return GOOGLE_CLIENT_IDS.web;
}
